import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../../Moduels/Context/AuthContext";
import { useToast } from "../../../Moduels/Context/ToastContext";
import { fetchShiftTemplates } from "../../../Moduels/attendance/shiftManagementApi";
import {
  assignShiftAllocation,
  bulkShiftAllocation,
  changeShiftAllocation,
  endShiftAllocation,
  fetchShiftAllocations,
  fetchShiftOperationsSummary,
} from "../../../Moduels/attendance/shiftAllocationApi";
import { fetchWfAttendanceProfile } from "../../../Moduels/attendance/wfApi";
import {
  formatAttendanceSourceLabel,
  formatWorkScheduleSummary,
  isShiftAttendanceEnabled,
  resolveCurrentSource,
} from "./attendanceConfigurationMeta";
import WfEmployeeMultiSelect from "../../roster/WfEmployeeMultiSelect";
import WfShiftSelect from "../../roster/WfShiftSelect";
import DatePickerField from "../../common/DatePickerField";
import { rosterTheme as t } from "../../roster/rosterTheme";
import { formatTime12 } from "../../roster/rosterUtils";
import API from "../../../services/api";

const TABS = [
  { id: "current", label: "Current Shifts" },
  { id: "allocation", label: "Shift Allocation" },
  { id: "upcoming", label: "Upcoming" },
  { id: "history", label: "History" },
];

const SETTINGS_SHIFT_CREATE = "/settings/attendance-leave-setup?tab=shifts";
const SETTINGS_ATTENDANCE_METHOD = "/settings/attendance-leave-setup?tab=method";
const ATTENDANCE_CONFIGURATION = "/attendance/configuration";

function errMsg(e) {
  const d = e.response?.data?.detail;
  if (d && typeof d === "object" && d.message) return d.message;
  if (d && typeof d === "object" && d.code) return String(d.message || d.code);
  if (typeof d === "string") {
    if (d.toLowerCase() === "not found") {
      return "Shift assignment API is unavailable. Restart the backend (latest code) and run: alembic upgrade head";
    }
    return d;
  }
  const status = e.response?.status;
  if (status === 404) {
    return "Shift assignment API not found — restart the backend on the latest code and run database migrations.";
  }
  const msg = e.message || "Request failed";
  if (String(msg).toLowerCase().includes("not found")) {
    return "Shift assignment API not found — restart the backend on the latest code and run database migrations.";
  }
  return msg;
}

function groupAllocationsByTemplate(items = []) {
  const map = {};
  for (const row of items) {
    const tid = row.template_id;
    if (!tid) continue;
    if (!map[tid]) map[tid] = [];
    map[tid].push(row);
  }
  return map;
}

const EMPTY_ASSIGN_FORM = {
  template_id: "",
  effective_from: "",
  effective_to: "",
  employeePicks: [],
};

function templateStatus(row) {
  const life = row?.config_json?.lifecycle_status || row?.status;
  if (life) return life;
  return row?.is_active ? "active" : "inactive";
}

function canAssignTemplate(row) {
  return templateStatus(row) === "active" && row?.is_active !== false;
}

function summaryFromTemplates(items) {
  const list = items || [];
  const active = list.filter((s) => templateStatus(s) === "active" && s?.is_active !== false);
  const employees_per_template = {};
  let employees_assigned = 0;
  for (const s of list) {
    const tid = s.template_id;
    const n = Number(s.employees_assigned) || 0;
    if (tid) employees_per_template[tid] = n;
    employees_assigned += n;
  }
  const running = active.filter((t) => (employees_per_template[t.template_id] || 0) > 0).length;
  return {
    total_shifts: list.length,
    active_shifts: active.length,
    employees_assigned,
    unassigned_employees: 0,
    upcoming_allocations: 0,
    current_running_shifts: running,
    employees_per_template,
    active_employee_count: 0,
  };
}

export default function WfShiftManagement() {
  const { role } = useContext(AuthContext);
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const isMutator = ["admin", "hr", "superadmin", "super_admin"].includes(
    String(role || "").toLowerCase()
  );

  const [profile, setProfile] = useState(null);
  const [attendanceSource, setAttendanceSource] = useState("");
  const [summary, setSummary] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [allocTotal, setAllocTotal] = useState(0);
  const [tab, setTab] = useState("current");
  const [search, setSearch] = useState("");
  const [filterTemplate, setFilterTemplate] = useState(searchParams.get("shift") || "");
  const [loading, setLoading] = useState(true);
  const [allocApiUnavailable, setAllocApiUnavailable] = useState(false);
  const [rosterByTemplate, setRosterByTemplate] = useState({});

  const [assignOpen, setAssignOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [changeRow, setChangeRow] = useState(null);
  const [assignForm, setAssignForm] = useState({ ...EMPTY_ASSIGN_FORM });
  const [bulkForm, setBulkForm] = useState({ ...EMPTY_ASSIGN_FORM });
  const [changeForm, setChangeForm] = useState({ template_id: "", effective_from: "", effective_to: "" });
  const [saving, setSaving] = useState(false);

  const shiftFeaturesOn = Boolean(
    profile?.shift_features_enabled ?? isShiftAttendanceEnabled(profile)
  );
  const canOperate = shiftFeaturesOn && isMutator;

  const loadMeta = useCallback(async () => {
    const [prof, hrRes] = await Promise.all([
      fetchWfAttendanceProfile(),
      API.get("/organisation/me/hr-settings").catch(() => ({ data: {} })),
    ]);
    const hrAtt = hrRes?.data?.attendance_settings || hrRes?.data?.hr_settings?.attendance || {};
    setProfile(prof);
    setAttendanceSource(resolveCurrentSource({}, hrAtt, prof));
    return prof;
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setAllocApiUnavailable(false);
    try {
      await loadMeta();
      const bucket =
        tab === "upcoming" ? "upcoming" : tab === "history" ? "historical" : tab === "current" ? "current" : undefined;
      const needAllocationList = tab !== "current";

      const [sumSettled, shiftSettled, rosterSettled, allocSettled] = await Promise.allSettled([
        fetchShiftOperationsSummary(),
        fetchShiftTemplates({ page: 1, page_size: 200 }),
        fetchShiftAllocations({
          bucket: "current",
          page: 1,
          page_size: 500,
        }),
        needAllocationList
          ? fetchShiftAllocations({
              search: search || undefined,
              template_id: filterTemplate || undefined,
              bucket,
              page: 1,
              page_size: 200,
            })
          : Promise.resolve(null),
      ]);

      let shiftItems = [];
      if (shiftSettled.status === "fulfilled") {
        shiftItems = shiftSettled.value.items || [];
        setShifts(shiftItems);
      } else {
        setShifts([]);
        toast.error(errMsg(shiftSettled.reason));
        setSummary(null);
        setAllocations([]);
        setAllocTotal(0);
        return;
      }

      if (sumSettled.status === "fulfilled") {
        setSummary(sumSettled.value);
      } else {
        setSummary(shiftItems.length ? summaryFromTemplates(shiftItems) : null);
      }

      if (rosterSettled.status === "fulfilled") {
        setRosterByTemplate(groupAllocationsByTemplate(rosterSettled.value?.items || []));
        setAllocApiUnavailable(false);
      } else {
        setRosterByTemplate({});
      }

      if (needAllocationList) {
        if (allocSettled.status === "fulfilled" && allocSettled.value) {
          setAllocations(allocSettled.value.items || []);
          setAllocTotal(allocSettled.value.total || 0);
        } else {
          setAllocations([]);
          setAllocTotal(0);
          setAllocApiUnavailable(true);
        }
      } else {
        setAllocations([]);
        setAllocTotal(0);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [filterTemplate, loadMeta, search, tab, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (filterTemplate) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("shift", filterTemplate);
        return next;
      });
    }
  }, [filterTemplate, setSearchParams]);

  const shiftOptions = useMemo(
    () =>
      (shifts || []).map((s) => ({
        template_id: s.template_id,
        shift_id: s.template_id,
        code: s.code,
        name: `${s.code} — ${s.name}`,
        start_time: s.start_time,
        end_time: s.end_time,
        break_minutes: s.break_minutes,
        is_active: s.is_active,
        config_json: s.config_json,
      })),
    [shifts]
  );

  const currentShiftCards = useMemo(() => {
    const counts = summary?.employees_per_template || {};
    return (shifts || []).map((s) => ({
      ...s,
      assigned: counts[s.template_id] || 0,
    }));
  }, [shifts, summary]);

  const gateState = useMemo(() => {
    if (!shiftFeaturesOn) return "shift_disabled";
    if (!(shifts || []).length) return "no_shifts";
    if (summary && summary.active_employee_count > 0 && summary.unassigned_employees === summary.active_employee_count) {
      return "operational";
    }
    return "operational";
  }, [shiftFeaturesOn, shifts, summary]);

  const openSettingsCreate = () => {
    navigate(
      `/settings/attendance-leave-setup?tab=shifts&edit=1&create=1&returnTo=${encodeURIComponent("/attendance/shifts")}`
    );
  };

  const refreshAfterMutation = async () => {
    await loadData();
    window.dispatchEvent(new Event("attendance-profile-changed"));
  };

  const handleAssign = async (formState = assignForm) => {
    if (!canOperate) return;
    const { template_id, effective_from, effective_to, employeePicks } = formState;
    const employeeIds = [...new Set((employeePicks || []).map((p) => String(p.employee_id)))];
    if (!template_id || !effective_from || !employeeIds.length) {
      toast.error("Select shift, effective date, and at least one employee.");
      return;
    }
    setSaving(true);
    try {
      if (employeeIds.length === 1) {
        await assignShiftAllocation({
          employee_id: employeeIds[0],
          template_id,
          effective_from,
          effective_to: effective_to || undefined,
        });
        toast.success("Employee assigned to shift.");
      } else {
        const res = await bulkShiftAllocation({
          template_id,
          employee_ids: employeeIds,
          effective_from,
          effective_to: effective_to || undefined,
        });
        toast.success(`Assigned ${res.created_count} employee(s).`);
        if (res.failed?.length) {
          const sample = res.failed
            .slice(0, 3)
            .map((f) => f.message || f.code)
            .join("; ");
          toast.error(`${res.failed.length} failed — ${sample}`);
        }
      }
      setAssignOpen(false);
      setBulkOpen(false);
      setAssignForm({ ...EMPTY_ASSIGN_FORM });
      setBulkForm({ ...EMPTY_ASSIGN_FORM });
      await refreshAfterMutation();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const handleChangeShift = async () => {
    if (!canOperate || !changeRow) return;
    setSaving(true);
    try {
      await changeShiftAllocation({
        employee_id: changeRow.employee_id,
        template_id: changeForm.template_id,
        effective_from: changeForm.effective_from,
        effective_to: changeForm.effective_to || undefined,
        reason: "Shift change from operational shift management",
      });
      toast.success("Shift updated; previous allocation closed.");
      setChangeRow(null);
      await refreshAfterMutation();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (row) => {
    if (!canOperate) return;
    if (!window.confirm(`Remove ${row.employee_name || "employee"} from shift effective today?`)) return;
    try {
      await endShiftAllocation(row.assignment_id, {});
      toast.success("Employee removed from shift (allocation ended).");
      await refreshAfterMutation();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const disabledHint = !shiftFeaturesOn
    ? "Enable shift-based work schedule to perform this action."
    : !isMutator
      ? "View only — HR or Admin required to assign shifts."
      : "";

  return (
    <div className={t.page}>
      <header className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Shift Management</h1>
          <p className={`${t.cardSub} mt-1`}>Allocate and manage employee work shifts.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={t.btnSecondary}
            onClick={openSettingsCreate}
            title="Shift definitions are configured in Settings"
          >
            + Create Shift
          </button>
          <button
            type="button"
            className={t.btnPrimary}
            disabled={!canOperate}
            title={disabledHint}
            onClick={() => setAssignOpen(true)}
          >
            Assign Employees
          </button>
          <button
            type="button"
            className={t.btnSecondary}
            disabled={!canOperate}
            title={disabledHint}
            onClick={() => {
              setBulkOpen(true);
              setAssignOpen(true);
            }}
          >
            Bulk Assign
          </button>
        </div>
      </header>

      {!shiftFeaturesOn && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 mb-4 text-sm text-amber-950">
          <p className="font-semibold">Shift-based attendance is not enabled</p>
          <p className="mt-1">
            Shifts you define under <strong>Settings → Attendance → Shift Management</strong> appear here for
            review. Employee assignment and runtime shift rules apply only after you turn on{" "}
            <strong>Shift roster</strong> or a shift work schedule (Biometric and other capture methods stay
            unchanged).
          </p>
          {(shifts || []).length > 0 && (
            <p className="mt-2 font-medium">
              {shifts.length} shift definition{(shifts.length === 1 ? "" : "s")} loaded from your organisation
              master.
            </p>
          )}
          <p className="mt-2 text-xs text-amber-800">
            Current capture method: {formatAttendanceSourceLabel(attendanceSource, profile) || "—"} · Work
            schedule: {formatWorkScheduleSummary(profile)}
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Link to={ATTENDANCE_CONFIGURATION} className={`${t.btnPrimary} !text-sm`}>
              Enable shift schedule
            </Link>
            <Link to={SETTINGS_SHIFT_CREATE} className={`${t.btnSecondary} !text-sm`}>
              Edit shifts in Settings
            </Link>
            <Link to={SETTINGS_ATTENDANCE_METHOD} className={`${t.btnSecondary} !text-sm`}>
              Attendance method
            </Link>
          </div>
        </div>
      )}

      {shiftFeaturesOn && gateState === "no_shifts" && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 mb-4 text-sm">
          <p className="font-medium text-slate-800">No shifts have been configured yet.</p>
          <p className="text-slate-600 mt-1">Create shift definitions in Settings — they appear here automatically.</p>
          <button type="button" className={`${t.btnPrimary} mt-3`} onClick={openSettingsCreate}>
            Create Shift in Settings
          </button>
        </div>
      )}

      {!canOperate && shiftFeaturesOn && (
        <div className="rounded-lg bg-sky-50 border border-sky-100 px-4 py-2 text-sm text-sky-900 mb-4">
          View-only: browse allocations and shift coverage. HR/Admin can assign or change shifts when shift
          schedule is enabled.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        {[
          ["Total Shifts", summary?.total_shifts ?? "—"],
          ["Active Shifts", summary?.active_shifts ?? "—"],
          ["Employees Assigned", summary?.employees_assigned ?? "—"],
          ["Unassigned", summary?.unassigned_employees ?? "—"],
          ["Upcoming", summary?.upcoming_allocations ?? "—"],
          ["Running Shifts", summary?.current_running_shifts ?? "—"],
        ].map(([label, val]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="text-xs text-slate-500">{label}</div>
            <div className="text-xl font-semibold text-slate-900">{loading ? "…" : val}</div>
          </div>
        ))}
      </div>

      <div className={`${t.cardPad} space-y-3`}>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            className={`${t.input} !py-1.5 max-w-xs`}
            placeholder="Search employee or shift…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className={`${t.select} !py-1.5 w-auto`}
            value={filterTemplate}
            onChange={(e) => setFilterTemplate(e.target.value)}
          >
            <option value="">All shifts</option>
            {shifts.map((s) => (
              <option key={s.template_id} value={s.template_id}>
                {s.code} — {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-2">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              type="button"
              className={
                tab === tb.id
                  ? `${t.btnPrimary} !py-1.5 !px-3 !text-sm`
                  : `${t.btnGhost} !py-1.5 !px-3 !text-sm`
              }
              onClick={() => setTab(tb.id)}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-500">Loading shift data…</div>
        ) : tab === "current" ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {currentShiftCards.map((row) => (
              <div
                key={row.template_id}
                className="border border-slate-200 rounded-xl p-4"
                style={{ borderLeftWidth: 4, borderLeftColor: row.color_tag || "#0d9488" }}
              >
                <div className="font-semibold text-slate-900">
                  {row.code} — {row.name}
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  {formatTime12(row.start_time)} → {formatTime12(row.end_time)}
                  {row.break_minutes ? ` · Break ${row.break_minutes}m` : ""}
                </p>
                <p className="text-xs text-slate-500 mt-1 capitalize">Status: {templateStatus(row)}</p>
                <p className="text-sm font-medium mt-2">
                  Assigned employees:{" "}
                  {(rosterByTemplate[row.template_id] || []).length || row.assigned || 0}
                </p>
                {(rosterByTemplate[row.template_id] || []).length > 0 ? (
                  <ul className="mt-2 space-y-1 max-h-28 overflow-auto text-xs text-slate-700 bg-slate-50 rounded-md p-2 border border-slate-100">
                    {(rosterByTemplate[row.template_id] || []).map((emp) => (
                      <li key={emp.assignment_id} className="flex justify-between gap-2">
                        <span className="truncate">
                          <span className="font-medium">{emp.employee_name}</span>
                          {emp.employee_code ? (
                            <span className="text-slate-500"> · {emp.employee_code}</span>
                          ) : null}
                        </span>
                        <span className="text-slate-500 shrink-0">{emp.effective_from}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">No employees on this shift yet.</p>
                )}
                <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    className={`${t.btnGhost} !text-xs`}
                    onClick={() => {
                      setFilterTemplate(String(row.template_id));
                      setTab("allocation");
                    }}
                  >
                    View all
                  </button>
                  <button
                    type="button"
                    className={`${t.btnGhost} !text-xs`}
                    disabled={!canOperate || !canAssignTemplate(row)}
                    title={disabledHint}
                    onClick={() => {
                      setAssignForm({ ...EMPTY_ASSIGN_FORM, template_id: String(row.template_id) });
                      setBulkOpen(false);
                      setAssignOpen(true);
                    }}
                  >
                    Assign
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-auto">
            {allocApiUnavailable && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-950 text-sm px-3 py-2 mb-3">
                Employee allocation list is temporarily unavailable. Restart the API server on the latest code and
                run <code className="text-xs">alembic upgrade head</code> if this persists. Shift cards above still
                show definitions from Settings.
              </div>
            )}
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-2 text-left">Employee</th>
                  <th className="p-2 text-left">Department</th>
                  <th className="p-2 text-left">Current shift</th>
                  <th className="p-2 text-left">Timing</th>
                  <th className="p-2 text-left">Effective from</th>
                  <th className="p-2 text-left">Effective to</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {allocations.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-slate-500">
                      {tab === "allocation"
                        ? "No employees are currently assigned to shifts."
                        : "No records for this view."}
                    </td>
                  </tr>
                ) : (
                  allocations.map((row) => (
                    <tr key={row.assignment_id} className="border-t border-slate-100">
                      <td className="p-2">
                        <div className="font-medium">{row.employee_name}</div>
                        <div className="text-xs text-slate-500">{row.employee_code}</div>
                      </td>
                      <td className="p-2 text-xs text-slate-600">
                        {row.department_name || "—"}
                        {row.designation_title ? (
                          <div className="text-slate-500">{row.designation_title}</div>
                        ) : null}
                      </td>
                      <td className="p-2">
                        {row.shift_code} — {row.shift_name}
                      </td>
                      <td className="p-2">
                        {formatTime12(row.start_time)} – {formatTime12(row.end_time)}
                      </td>
                      <td className="p-2">{row.effective_from}</td>
                      <td className="p-2">{row.effective_to || "Open"}</td>
                      <td className="p-2 capitalize">{row.allocation_status}</td>
                      <td className="p-2 text-right space-x-1">
                        {row.allocation_status !== "historical" && (
                          <>
                            <button
                              type="button"
                              className={`${t.btnGhost} !text-xs`}
                              disabled={!canOperate}
                              onClick={() => {
                                setChangeRow(row);
                                setChangeForm({
                                  template_id: row.template_id || "",
                                  effective_from: "",
                                  effective_to: "",
                                });
                              }}
                            >
                              Change
                            </button>
                            <button
                              type="button"
                              className={`${t.btnGhost} !text-xs !text-red-600`}
                              disabled={!canOperate}
                              onClick={() => handleRemove(row)}
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <p className="text-xs text-slate-500 pt-2">{allocTotal} record(s)</p>
          </div>
        )}
      </div>

      {(assignOpen || bulkOpen) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-5 space-y-3">
            <h2 className="text-lg font-semibold">{bulkOpen ? "Bulk assign employees" : "Assign employees to shift"}</h2>
            <p className="text-xs text-slate-500">
              Search each employee and add them to the list below. Review names before confirming.
            </p>
            <label className="block text-sm">
              Shift
              <WfShiftSelect
                shifts={shiftOptions}
                value={(bulkOpen ? bulkForm : assignForm).template_id}
                onChange={(v) =>
                  bulkOpen
                    ? setBulkForm((f) => ({ ...f, template_id: v }))
                    : setAssignForm((f) => ({ ...f, template_id: v }))
                }
                disabled={!canOperate}
              />
            </label>
            <label className="block text-sm">
              Effective from
              <DatePickerField
                value={(bulkOpen ? bulkForm : assignForm).effective_from}
                onChange={(v) =>
                  bulkOpen
                    ? setBulkForm((f) => ({ ...f, effective_from: v }))
                    : setAssignForm((f) => ({ ...f, effective_from: v }))
                }
                disabled={!canOperate}
              />
            </label>
            <label className="block text-sm">
              Effective to (optional)
              <DatePickerField
                value={(bulkOpen ? bulkForm : assignForm).effective_to}
                onChange={(v) =>
                  bulkOpen
                    ? setBulkForm((f) => ({ ...f, effective_to: v }))
                    : setAssignForm((f) => ({ ...f, effective_to: v }))
                }
                disabled={!canOperate}
              />
            </label>
            <div className="block text-sm space-y-1">
              <span className="font-medium text-slate-800">Employees to assign</span>
              <WfEmployeeMultiSelect
                value={(bulkOpen ? bulkForm : assignForm).employeePicks}
                onChange={(picks) =>
                  bulkOpen
                    ? setBulkForm((f) => ({ ...f, employeePicks: picks }))
                    : setAssignForm((f) => ({ ...f, employeePicks: picks }))
                }
                disabled={!canOperate}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className={t.btnSecondary}
                onClick={() => {
                  setAssignOpen(false);
                  setBulkOpen(false);
                  setAssignForm({ ...EMPTY_ASSIGN_FORM });
                  setBulkForm({ ...EMPTY_ASSIGN_FORM });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={t.btnPrimary}
                disabled={!canOperate || saving}
                onClick={async () => {
                  await handleAssign(bulkOpen ? bulkForm : assignForm);
                  setBulkOpen(false);
                }}
              >
                {saving ? "Saving…" : "Confirm assignment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {changeRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-3">
            <h2 className="text-lg font-semibold">Change shift — {changeRow.employee_name}</h2>
            <WfShiftSelect
              shifts={shiftOptions}
              value={changeForm.template_id}
              onChange={(v) => setChangeForm((f) => ({ ...f, template_id: v }))}
              disabled={!canOperate}
            />
            <DatePickerField
              label="New effective from"
              value={changeForm.effective_from}
              onChange={(v) => setChangeForm((f) => ({ ...f, effective_from: v }))}
              disabled={!canOperate}
            />
            <DatePickerField
              label="Effective to (optional)"
              value={changeForm.effective_to}
              onChange={(v) => setChangeForm((f) => ({ ...f, effective_to: v }))}
              disabled={!canOperate}
            />
            <div className="flex justify-end gap-2">
              <button type="button" className={t.btnSecondary} onClick={() => setChangeRow(null)}>
                Cancel
              </button>
              <button
                type="button"
                className={t.btnPrimary}
                disabled={!canOperate || saving}
                onClick={handleChangeShift}
              >
                Save change
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
